import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cabinet } from '../../api/cabinet';
import { useCaseSite } from '../../app/caseSite';
import { ErrorBlock } from '../../components/ErrorBlock';
import { BackIcon } from '../../components/Icons';
import { Loading } from '../../components/Loading';
import { useI18n, useT } from '../../i18n';
import { formatDate } from '../../lib/format';
import { loc } from '../../lib/loc';
import { useAsync, useOnVisible } from '../../lib/useAsync';
import { ChatSection } from '../chat/ChatSection';
import { CaseFeedbackCard } from '../feedback/CaseFeedbackCard';
import { DocumentsSection } from './DocumentsSection';
import { CaseNowLine, FamilySteps, useFamilySteps } from './FamilySteps';
import { openItems, splitDocuments } from './model';
import { NeedsSection } from './NeedsSection';
import { TimelineSection } from './TimelineSection';

export function CasePage() {
  const { id = '' } = useParams();
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const { data, error, loading, reload } = useAsync(() => cabinet.caseDetail(id), [id]);
  useOnVisible(() => void reload());
  const familySteps = useFamilySteps(id);
  const publishSite = useCaseSite((s) => s.publish);
  const clearSite = useCaseSite((s) => s.clear);
  const owner = `case:${id}`;
  const site = data && data.id === id ? data.site : undefined;
  // контакти й мова сайту справи (MD-справа → ro/ru і молдовські контакти); при виході зі сторінки — прибираємо
  useEffect(() => {
    if (site !== undefined) publishSite(owner, site);
  }, [owner, site, publishSite]);
  useEffect(() => () => clearSite(owner), [owner, clearSite]);

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBlock error={error} onRetry={() => void reload()} />;
  if (!data) return null;

  const docs = splitDocuments(data);
  const open = openItems(data.checklist).length;
  const anchors = [
    { href: '#stages', label: t('case.stages') },
    { href: '#needs', label: open ? `${t('case.needs')} · ${open}` : t('case.needs') },
    { href: '#documents', label: t('docs.fromUs') },
    { href: '#chat', label: t('chat.title') },
    { href: '#history', label: t('timeline.title') },
  ];

  return (
    <>
      <Link to="/cases" className="link -mt-3 mb-4 gap-2 no-underline">
        <BackIcon /> {t('nav.cases')}
      </Link>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[14px] text-lab">
        <span>{loc(data.service_type?.name, locale)}</span>
        {data.number != null ? <span>{t('cases.number', { n: data.number })}</span> : null}
      </div>
      <h1 className="mt-3 text-[28px] leading-tight sm:text-[34px]">{data.title || loc(data.service_type?.name, locale)}</h1>
      <CaseNowLine steps={familySteps.steps} loading={familySteps.loading} state={data.state} />
      <p className="mt-1 text-[13px] text-lab">{t('cases.updated', { date: formatDate(data.updated_at, locale) })}</p>

      {/* переносимо в рядки, а не ховаємо за правим краєм: на телефоні всі розділи видно одразу */}
      <nav aria-label={t('case.sections')} className="mt-6">
        <ul className="flex flex-wrap gap-2">
          {anchors.map((a) => (
            <li key={a.href}>
              <a href={a.href} className="inline-flex min-h-tap items-center border border-line px-4 text-[14px] text-txt hover:border-fg hover:text-fg">
                {a.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="mt-10 flex flex-col gap-14">
        <section aria-labelledby="stages-h" id="stages" className="scroll-mt-6">
          <h2 id="stages-h" className="sec-h">
            {t('case.stages')}
          </h2>
          <FamilySteps caseId={data.id} states={data.states ?? []} />
        </section>
        <CaseFeedbackCard caseId={data.id} />
        <NeedsSection caseId={data.id} checklist={data.checklist} onChanged={() => void reload()} />
        <DocumentsSection fromUs={docs.fromUs} yours={docs.yours} />
        <ChatSection caseId={data.id} />
        <TimelineSection detail={data} />
      </div>
    </>
  );
}
